import { inject } from '@angular/core';
import { CanActivateFn, ParamMap, Params, Router, Routes } from '@angular/router';

export const overviewDetailTitles = {
  revenue: 'Revenue history', profit: 'Profitability', cash: 'Cash flow',
  position: 'Financial position', analysis: 'Analysis',
} as const;
export type OverviewView = 'overview' | keyof typeof overviewDetailTitles;

export const overviewRouteData = {
  title: 'Business Insight', breadcrumbRoot: '/overview', breadcrumbShowRoot: true,
  breadcrumbRootQueryParams: ['interval', 'period', 'compare', 'metric'],
};

/** Overview list/view context kept when another surface opens a saved analysis from Overview.
 *  The analysis supplies its own report and period, so those parameters are not carried. */
export function overviewAnalysisContext(query: ParamMap): Params {
  return Object.fromEntries(overviewRouteData.breadcrumbRootQueryParams
    .filter(key => !['interval', 'period'].includes(key) && query.has(key)).map(key => [key, query.get(key)!]));
}

// Like Files, keep the owning page mounted while its child path changes.
export const overviewChildRoutes: Routes = [
  { path: '', pathMatch: 'full', children: [] },
  { path: 'assumptions', pathMatch: 'full', redirectTo: '' },
  // The attribution-driven detail pages were removed; keep their old links reachable at Overview.
  { path: 'growth', pathMatch: 'full', redirectTo: '' },
  { path: 'performance', pathMatch: 'full', redirectTo: '' },
  { path: 'customer', pathMatch: 'full', redirectTo: '' },
  // Saved reports follow the list / new / :id shape of Leads and Customers; old analysis links still open.
  { path: 'analysis/:analysisId', redirectTo: 'reports/:analysisId' },
  ...Object.entries(overviewDetailTitles).map(([path, title]) => ({ path: path === 'analysis' ? 'reports/:analysisId' : path, data: { title }, children: [] })),
];

/** Preserve old mock links without retaining query-based page selection. */
export const redirectLegacyOverviewView: CanActivateFn = route => {
  const { view, ...queryParams } = route.queryParams;
  if (view === undefined) return true;
  const childPath = route.firstChild?.url[0]?.path;
  const target = childPath ?? (view !== 'analysis' && Object.prototype.hasOwnProperty.call(overviewDetailTitles, view) ? view : '');
  const segments = target === 'analysis' && route.firstChild?.paramMap.get('analysisId')
    ? ['/overview', 'reports', route.firstChild.paramMap.get('analysisId')!] : target ? ['/overview', target] : ['/overview'];
  return inject(Router).createUrlTree(segments, {
    queryParams, fragment: route.fragment ?? undefined,
  });
};
